<template lang="pug">
	.multi-select(
		:class="{'multi-select--error' : hasError, 'multi-select--open' : opened}"
		@keyup.down="moveDown"
		@keyup.up="moveUp"
		@keyup.esc="close"
		:style="{maxWidth: maxWidth + 'px'}"
	)
		label.sr-only(v-if="label", :id="labelId") {{label}}
		button.multi-select__button(
			:class="{'multi-select__button--open': opened}"
			ref="button"
			type="button"
			:aria-haspopup="`${id}-listbox`"
			v-bind="$attrs"
			:aria-labelledby="ariaLabelledBy"
			:id="`${id}-button`"
			:aria-expanded="opened"
			:aria-invalid="hasError"
			@click="toggleList"
			:aria-describedby="ariaDescribedBy"
		)
			p.button__text
				span.button__filter(v-if="label" aria-hidden="true") {{label}}
				span.button__option {{ getText() }}
			svg(aria-hidden="true" width='16' height='11' viewBox='0 0 16 11' fill='none' xmlns='http://www.w3.org/2000/svg')
				path(d='M8 0.666992L0 8.66699L1.88667 10.5537L8 4.44032L14.1133 10.5537L16 8.66699L8 0.666992Z' fill='#A1A1A1')
		.multi-select__options
			ul.options__list(
				v-show="opened"
				tabindex="-1"
				:role="`${id}-listbox`"
				aria-multiselectable="true"
				:aria-activedescendant="ariaActivedescendant"
			)
				li.options__item(
					v-for="(option, index) in options"
					ref="listItems"
					:id="idPrefix + 'opts-' + index"
					role="option"
					:aria-selected="value.includes(index + 1)"
					tabindex="0"
					@keyup.space="toggleItem()"
				)
					VueCheckbox(
						:value="model"
						@keyup.stop=""
						@keydown.down.capture="moveDown"
						@keydown.up.capture="moveUp"
						:checked-value="option.key"
						tabindex="-1"
						aria-hidden="true"
						@input="$emit('input', $event)"
						role="presentation"
						:background="option.color ? option.color : 'default'"
					) {{ option.value }}
</template>

<script>
import VueCheckbox from '@ui-common/vue-checkbox';
export default {
	inheritAttrs: false,
	name: 'VueMultiSelect',
	data: () => ({
		model: [],
		opened: false,
		selectedIndex: null,
		idPrefix: 'sf-ms-'
	}),
	props: {
		id: {
			required: true,
			type: String
		},
		labelId: {
			type: String,
			required: false
		},
		label: {
			type: String,
			required: false
		},
		value: {
			required: true
		},
		selectAll: {
			type: Boolean,
			default: false
		},
		options: {
			type: Array,
			required: true
		},
		maxWidth: {
			type: Number
		},
		hasError: {
			type: Boolean,
			required: false,
			default: false
		},
		placeholder: {
			type: String,
			required: false,
			default: 'Kérjük, válasszon!'
		}
	},
	inject: {
		providedInputId: {
			default: null
		},
		providedErrorId: {
			default: null
		}
	},
	components: {
		VueCheckbox
	},
	watch: {
		value: {
			handler() {
				this.setModel();
			},
			immediate: true
		},
		model(val) {
			this.$emit('input', val);
		},
		opened(val) {
			if (val) {
				window.addEventListener('keydown', this.disableSpaceScrolling);
			} else {
				window.removeEventListener('keydown', this.disableSpaceScrolling);
			}
		}
	},
	computed: {
		ariaLabelledBy() {
			return `${this.labelId} ${this.id}-button`;
		},
		ariaDescribedBy() {
			let items = [this.hasError ? this.providedErrorId : null, this.hasTooltip ? this.providedTooltipId : null];
			return items.filter((item) => item != undefined).join(' ');
		},
		ariaActivedescendant() {
			if (this.$refs.listItems && this.selectedIndex != null) {
				return this.$refs.listItems[this.selectedIndex].id;
			} else {
				return false;
			}
		}
	},
	methods: {
		getText() {
			let text = [];
			if (this.model.length == 1) {
				text = 'Egy érték kiválasztva';
			} else if (this.model.length > 1) {
				text = 'Több érték kiválasztva';
			} else {
				text = this.placeholder;
			}
			return text;
		},
		disableSpaceScrolling(e) {
			const SPACE = e.keyCode == 32;
			const DOWN_ARROW = e.keyCode == 40;
			const UP_ARROW = e.keyCode == 38;
			if (SPACE || DOWN_ARROW || UP_ARROW) {
				e.preventDefault();
			}
		},
		close() {
			if (this.opened) {
				this.opened = false;
				this.selectedIndex = null;
				this.$refs.button.focus();
			}
		},
		setModel() {
			this.model = this.value;
			this.$emit('input', this.model);
		},
		moveDown() {
			if (this.opened) {
				if (this.selectedIndex == null) {
					this.selectedIndex = 0;
				} else if (this.selectedIndex < this.options.length - 1) {
					this.selectedIndex = this.selectedIndex + 1;
				}
				this.$refs.listItems[this.selectedIndex].focus();
			}
		},
		moveUp() {
			if (this.opened) {
				if (this.selectedIndex == null) {
					this.selectedIndex = this.options.length - 1;
				} else if (this.selectedIndex > 0) {
					this.selectedIndex = this.selectedIndex - 1;
				}
				this.$refs.listItems[this.selectedIndex].focus();
			}
		},
		toggleItem() {
			const itemKey = this.options[this.selectedIndex].key;
			if (this.model.includes(itemKey)) {
				this.removeItem(itemKey);
			} else {
				this.addItem(itemKey);
			}
		},
		removeItem(itemKey) {
			this.model = this.model.filter((item) => item != itemKey);
		},
		addItem(itemKey) {
			this.model = [...this.model, itemKey];
		},
		toggleList() {
			this.opened = !this.opened;

			if (this.opened && this.$refs.listItems.length) {
				this.$nextTick(() => {
					this.selectedIndex = 0;
					this.$refs.listItems[0].focus();
				});
				window.ipEventBus.publish('multi-select-close-others', this.id);
			}
		},
		handleClose() {
			window.ipEventBus.subscribe('multi-select-close-others', (channel, id) => {
				if (this.id != id) {
					this.close();
				}
			});
		}
	},
	created() {
		if (this.selectAll) {
			this.model = this.options.map((item) => item.key);
		}

		if (!this.id) {
			throw new Error(`You must provide an ID to ${this.$vnode.tag}`);
		}
		this.handleClose();
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="css" src="@ui-common/vue-checkbox/dist/vue-checkbox.css">
</style>
<style lang="css" scoped>
@import '../../../common/variables.pcss';

.multi-select {
	border: 1px solid var(--grey-f5);
	box-sizing: border-box;
}

.multi-select__button {
	width: 100%;
	margin: 0;
	border: 0;
	height: 68px;
	padding: 16px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: var(--grey-f5);

	&:focus {
		outline: none;
	}
}

.multi-select__button svg {
	transform: rotate(180deg);
}

.multi-select--open .multi-select__button svg {
	transform: none;
}

.button__text {
	display: flex;
	margin: 0;
	flex-direction: column;
	text-align: left;
}

.button__filter {
	font: 600 16px/20px 'regular', Arial, sans-serif;
	color: var(--black);
	text-transform: uppercase;
	margin-bottom: 2px;
}

.button__option {
	font: 16px/20px 'regular', Arial, sans-serif;
	color: var(--grey-75);
}

.multi-select--open {
	border-color: var(--grey-c5);
}

.multi-select--open,
.multi-select--open .multi-select__button {
	background-color: var(--white);
}

.multi-select__options {
	padding: 0 24px;
}
</style>
